import React, { useContext, useState } from 'react'
import moment from 'moment'
import { Alert, Button, Stack, Typography } from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import DocumentsField from '../../../../helpers/DocumentsField'
import {
  DEMO_UPLOAD_MESSAGE,
  fileRecord,
  uploadDocument,
} from '../../../../helpers/uploadDocument'
import { VehicleContext } from '../SingleVehicle'
import { SectionHeader } from './SectionKit'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Any other paperwork kept against the vehicle — leases, V5Cs, conversion
// certificates, photos. Same staged-save flow as the asset Attachments tab:
// removals can be undone, uploads go one at a time on save, and S3 objects
// are only deleted once the vehicle no longer references them.
const FilesSection = () => {
  const { vehicle, setVehicle } = useContext(VehicleContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [files, setFiles] = useState([])
  const [removed, setRemoved] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const existing = (vehicle.files ?? []).filter((f) => f?.key)
  const existingKeys = existing.map((f) => f.key)
  const recordFor = (key) => existing.find((f) => f.key === key)
  const dirty = files.length > 0 || removed.length > 0

  const toggleRemove = (key) =>
    setRemoved((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    )

  const discard = () => {
    setFiles([])
    setRemoved([])
    setError(null)
  }

  const save = async () => {
    if (saving || !dirty) return
    setSaving(true)
    setError(null)
    try {
      // One at a time - presigned URLs only live for 60s
      const uploaded = []
      for (const file of files) {
        const key = await uploadDocument(file, 'fleetequipment/vehicles')
        uploaded.push({
          ...fileRecord(file, key),
          uploadedBy: user._id,
          createdAt: new Date(),
        })
      }
      const nextFiles = [
        ...existing.filter((f) => !removed.includes(f.key)),
        ...uploaded,
      ]
      const res = await API.put(`/fleet/${vehicle._id}`, {
        update: { files: nextFiles },
      })
      if (res.status !== 200) throw new Error('Save failed')

      await Promise.allSettled(
        removed.map((key) => API.post('/deletes3object', { key })),
      )

      setVehicle((prev) => ({ ...prev, files: nextFiles }))
      setFiles([])
      setRemoved([])
      setToast({ message: 'Files updated' })
    } catch (err) {
      console.error('Unable to save vehicle files', err)
      setError(
        err?.message === DEMO_UPLOAD_MESSAGE
          ? DEMO_UPLOAD_MESSAGE
          : files.length
            ? 'Unable to save - a file may have failed to upload. Check your connection and try again.'
            : 'Unable to save files. Please try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  const pending = [
    files.length > 0 && `${plural(files.length, 'file')} to upload`,
    removed.length > 0 && `${plural(removed.length, 'file')} to remove`,
  ].filter(Boolean)

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Files"
        caption="Any other documents for this vehicle — leases, V5C, conversion paperwork, photos"
      />
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <DocumentsField
        existingDocuments={existingKeys}
        removedDocuments={removed}
        onToggleRemove={toggleRemove}
        files={files}
        onFilesChange={setFiles}
        disabled={saving}
        accept={null}
        maxSizeMB={50}
        caption="Any file type, up to 50 MB each"
        acceptLabel="files"
        existingName={(key) => recordFor(key)?.name}
        existingLabel={(key, index) => {
          const record = recordFor(key)
          const name = record?.name || `File ${index + 1}`
          return record?.createdAt
            ? `${name} · added ${moment(record.createdAt).format('D MMM YYYY')}`
            : name
        }}
      />
      {!existing.length && !files.length && (
        <Typography variant="body2" color="text.disabled">
          No files uploaded for this vehicle yet.
        </Typography>
      )}
      {dirty && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1.5}
          sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.secondary">
            {saving && files.length ? 'Uploading…' : pending.join(' · ')}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button color="inherit" onClick={discard} disabled={saving}>
              Discard
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default FilesSection
